"use client";

import { useState } from "react";
import Link from "next/link";
import { useRegisterInitiate, useRegisterComplete } from "@/hooks/use-auth";
import { useSocialAuth } from "@/hooks/use-social-auth";
import { useMagicLinkPolling } from "@/hooks/use-magic-link-polling";
import { useTranslations } from "next-intl";
import { ShieldPlus, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { Label } from "@/components/ui/label";
import { AuthMethodPicker } from "@/components/auth/auth-method-picker";
import { VerificationCodeInput } from "@/components/auth/verification-code-input";
import {
  normalizePhone,
  validatePhone,
  type PhoneValidationError,
} from "@/lib/phone";

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

interface EmailErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
}

function validateEmail(
  email: string,
  password: string,
  confirmPassword: string,
): EmailErrors {
  const errors: EmailErrors = {};
  if (!email) {
    errors.email = "emailRequired";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = "emailInvalid";
  }
  if (!password) {
    errors.password = "passwordRequired";
  } else if (password.length < 8) {
    errors.password = "passwordMinLength";
  } else if (!/\d/.test(password)) {
    errors.password = "passwordNeedsNumber";
  }
  if (!confirmPassword) {
    errors.confirmPassword = "confirmPasswordRequired";
  } else if (password !== confirmPassword) {
    errors.confirmPassword = "passwordsMismatch";
  }
  return errors;
}

interface PhoneErrors {
  phone?: PhoneValidationError;
}

// ---------------------------------------------------------------------------
// Steps: choose → email/phone form → verification
// ---------------------------------------------------------------------------

type Step = "choose" | "email" | "phone" | "verification";

export default function RegisterPage() {
  const t = useTranslations("auth");
  const tc = useTranslations("common");

  const [step, setStep] = useState<Step>("choose");

  // Email state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [emailErrors, setEmailErrors] = useState<EmailErrors>({});

  // Phone state
  const [phone, setPhone] = useState("");
  const [phoneDisplay, setPhoneDisplay] = useState("");
  const [phoneErrors, setPhoneErrors] = useState<PhoneErrors>({});

  // Two-step registration
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [verificationMethod, setVerificationMethod] = useState<"email" | "phone">("email");

  // Mutations
  const initiate = useRegisterInitiate();
  const complete = useRegisterComplete();
  const social = useSocialAuth();

  // Detect cross-tab magic link completion
  useMagicLinkPolling(step === "verification" && verificationMethod === "email");

  // -- Social loading state for picker --
  const socialLoadingMethod = social.isGoogleLoading
    ? ("google" as const)
    : social.isFacebookLoading
      ? ("facebook" as const)
      : null;

  // -- Google credential (from rendered GIS button) --
  function handleGoogleCredential(credential: string) {
    social.handleGoogleToken(credential);
  }

  // -- Method picker handler --
  function handleMethodSelect(method: "facebook" | "email" | "phone") {
    if (method === "facebook") {
      social.loginWithFacebook();
    } else {
      setStep(method);
    }
  }

  // -- Back to method picker --
  function backToChoose() {
    setStep("choose");
    setSessionId(null);
    initiate.reset();
    complete.reset();
  }

  // -- Email registration --
  function doEmailRegister() {
    const errs = validateEmail(email, password, confirmPassword);
    setEmailErrors(errs);
    if (Object.keys(errs).length > 0) return;

    initiate.mutate(
      { authMethod: "email_auth", fields: { email, password } },
      {
        onSuccess: (data) => {
          setSessionId(data.sessionId);
          if (data.requiresVerification) {
            setVerificationMethod("email");
            setStep("verification");
          } else {
            complete.mutate({ sessionId: data.sessionId });
          }
        },
      },
    );
  }

  // -- Phone registration --
  function doPhoneRegister() {
    const phoneError = validatePhone(phone);
    setPhoneErrors(phoneError ? { phone: phoneError } : {});
    if (phoneError) return;

    const normalizedPhone = normalizePhone(phone);
    initiate.mutate(
      { authMethod: "phone_auth", fields: { phone: normalizedPhone } },
      {
        onSuccess: (data) => {
          setSessionId(data.sessionId);
          if (data.requiresVerification) {
            setVerificationMethod("phone");
            setStep("verification");
          } else {
            complete.mutate({ sessionId: data.sessionId });
          }
        },
      },
    );
  }

  // -- Verification --
  function handleVerify(code: string) {
    if (!sessionId) return;
    complete.mutate({ sessionId, verificationCode: code });
  }

  const serverErrors = initiate.error?.errors;

  // =========================================================================
  // Brand header (shared)
  // =========================================================================

  const brand = (
    <Link href="/app" className="mb-8 flex flex-col items-center gap-2">
      <ShieldPlus className="h-10 w-10 text-primary" />
      <h1 className="text-xl font-semibold tracking-tight">{tc("appName")}</h1>
      {step === "choose" && (
        <p className="text-sm text-muted-foreground">{tc("tagline")}</p>
      )}
    </Link>
  );

  // =========================================================================
  // Step: verification
  // =========================================================================

  if (step === "verification") {
    const credential =
      verificationMethod === "email" ? email : phoneDisplay || phone;

    return (
      <>
        {brand}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-semibold tracking-tight">
              {verificationMethod === "email"
                ? t("verifyTitle")
                : t("verifyPhoneTitle")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <VerificationCodeInput
              description={
                verificationMethod === "email"
                  ? t("verifyDescription", { email: credential })
                  : t("otpSentTo", { phone: credential })
              }
              isPending={complete.isPending}
              error={complete.error}
              onSubmit={handleVerify}
              onBack={backToChoose}
            />
          </CardContent>
        </Card>
      </>
    );
  }

  // =========================================================================
  // Step: email form
  // =========================================================================

  if (step === "email") {
    return (
      <>
        {brand}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-semibold tracking-tight">
              {t("signUpWithEmail")}
            </CardTitle>
            <CardDescription>{t("emailFormHint")}</CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => { e.preventDefault(); doEmailRegister(); }}
              className="space-y-5"
            >
              <div className="space-y-2">
                <Label htmlFor="email" className="text-base">{t("email")}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t("emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 text-base"
                  aria-invalid={!!(emailErrors.email || serverErrors?.email)}
                />
                {(emailErrors.email || serverErrors?.email?.[0]) && (
                  <p className="text-sm text-destructive">
                    {emailErrors.email ? t(emailErrors.email) : serverErrors?.email?.[0]}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-base">{t("password")}</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 text-base"
                  aria-invalid={!!(emailErrors.password || serverErrors?.password)}
                />
                {(emailErrors.password || serverErrors?.password?.[0]) && (
                  <p className="text-sm text-destructive">
                    {emailErrors.password ? t(emailErrors.password) : serverErrors?.password?.[0]}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-base">
                  {t("confirmPassword")}
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="h-12 text-base"
                  aria-invalid={!!emailErrors.confirmPassword}
                />
                {emailErrors.confirmPassword && (
                  <p className="text-sm text-destructive">
                    {t(emailErrors.confirmPassword)}
                  </p>
                )}
              </div>

              {initiate.error && !initiate.error.errors && (
                <p className="text-sm text-destructive">
                  {initiate.error.status === 0 ? tc("networkError") : initiate.error.message}
                </p>
              )}

              <Button
                className="h-12 w-full text-base whitespace-normal"
                disabled={initiate.isPending || complete.isPending}
              >
                {initiate.isPending || complete.isPending
                  ? t("creatingAccount")
                  : t("createAccountButton")}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="h-12 w-full gap-2 text-base"
                onClick={backToChoose}
                disabled={initiate.isPending}
              >
                <ArrowLeft className="size-5" />
                {t("otherMethods")}
              </Button>
            </form>
          </CardContent>
        </Card>
      </>
    );
  }

  // =========================================================================
  // Step: phone form
  // =========================================================================

  if (step === "phone") {
    return (
      <>
        {brand}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-semibold tracking-tight">
              {t("signUpWithPhone")}
            </CardTitle>
            <CardDescription>{t("phoneFormHint")}</CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => { e.preventDefault(); doPhoneRegister(); }}
              className="space-y-5"
            >
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-base">{t("phone")}</Label>
                <PhoneInput
                  id="phone"
                  value={phone}
                  onChange={(value, meta) => {
                    setPhone(value);
                    setPhoneDisplay(meta.inputValue);
                  }}
                  size="default"
                  aria-invalid={!!(phoneErrors.phone || serverErrors?.phone)}
                />
                {(phoneErrors.phone || serverErrors?.phone?.[0]) && (
                  <p className="text-sm text-destructive">
                    {phoneErrors.phone ? t(phoneErrors.phone) : serverErrors?.phone?.[0]}
                  </p>
                )}
              </div>

              {initiate.error && !initiate.error.errors && (
                <p className="text-sm text-destructive">
                  {initiate.error.status === 0 ? tc("networkError") : initiate.error.message}
                </p>
              )}

              <Button
                className="h-12 w-full text-base whitespace-normal"
                disabled={initiate.isPending || complete.isPending}
              >
                {initiate.isPending || complete.isPending
                  ? t("sendingCode")
                  : t("sendCode")}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="h-12 w-full gap-2 text-base"
                onClick={backToChoose}
                disabled={initiate.isPending}
              >
                <ArrowLeft className="size-5" />
                {t("otherMethods")}
              </Button>
            </form>
          </CardContent>
        </Card>
      </>
    );
  }

  // =========================================================================
  // Step: choose method
  // =========================================================================

  return (
    <>
      {brand}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-semibold tracking-tight">
            {t("createAccount")}
          </CardTitle>
          <CardDescription className="text-base">
            {t("chooseMethodDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <AuthMethodPicker
            mode="register"
            onSelect={handleMethodSelect}
            onGoogleCredential={handleGoogleCredential}
            loadingMethod={socialLoadingMethod}
            isFacebookReady={social.isFacebookReady}
            error={social.error}
          />

          <p className="text-center text-sm text-muted-foreground">
            {t("hasAccount")}{" "}
            <Link
              href="/login"
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              {t("signIn")}
            </Link>
          </p>
        </CardContent>
      </Card>
    </>
  );
}
